import { addDays, format, startOfToday } from 'date-fns';
import { ReactElement, useCallback, useEffect, useState } from 'react';

import { SERVER_FORMAT_DATE } from '@/common/app/constants/timeConstants';
import { mergeAvailability } from '@/common/app/utils/mergeAvailability';
import {
    IActivityWithAvailability,
    IActivityWithAvailabilityFull,
} from '@/common/domain/Merge.domain';
import { fetchAvailabilityShortByIds } from '@/common/service/api/Availability/Availability';
import { IActivity } from '@/entities/Activity/domain/Activity.domain';
import { ActivitiesPreview } from '@/entities/Activity/ui/ActivitiesPreview';
import { Layout } from '@/shared/Layout/ui/Layout';
import Empty from './Empty';
import * as S from './WishList.styles';
import { getActivities } from './service/APIwishlist';
import { getSubstringBetween } from '@/common/app/utils/stringUtils';
import { useWishlistContext } from '@/common/app/contexts';
import MetaWishList from './Meta/MetaWishlist';
import { fetchUserImagesInSearch } from '@/common/service/api/Activity/Activity';
import { TImageExcerpt } from '@/common/service/api/Activity/Activity.domain';
import { PayPalSection } from '@/common/ui/styled/PayPalSection/PayPalSection';
import { Preloader } from '@/common/ui/loaders/Preloader';
import { transformActivityToPreview } from '../ReviewsPage/helper';

export const Wishlist = (): ReactElement => {
    const [loading, setLoading] = useState(true);
    const [pageActivities, setPageActivities] = useState<
        IActivity[] | IActivityWithAvailability[] | IActivityWithAvailabilityFull[]
    >([]);
    const [userImages, setUserImages] = useState<TImageExcerpt>({} as TImageExcerpt);

    const { wishlist, isFetching } = useWishlistContext();

    const getActivityDescription = (activity: IActivity) => {
        if (!activity.description || !activity.description.length) {
            return getSubstringBetween(activity.data, '"description":"', ',"departure');
        }

        return activity.description;
    };

    const prepareWishlist = useCallback(async () => {
        if (isFetching) return;
        if (!Array.from(wishlist).length) {
            setPageActivities([]);
            setLoading(false);
            return;
        }
        try {
            const activitiesData = await getActivities(wishlist);
            const dayFrom = startOfToday();
            const activity_ids = wishlist.join(',');

            const params = {
                trip_ids: activity_ids,
                from: format(dayFrom, SERVER_FORMAT_DATE),
                to: format(addDays(dayFrom, 6), SERVER_FORMAT_DATE),
            };

            const [newImages, availables] = await Promise.all([
                fetchUserImagesInSearch(activity_ids),
                fetchAvailabilityShortByIds(params),
            ]);

            const activities = mergeAvailability(activitiesData, availables);

            setPageActivities(activities);
            setUserImages((prev) => ({ ...prev, ...newImages }));
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, [isFetching, wishlist]);

    useEffect(() => {
        prepareWishlist();
    }, [prepareWishlist]);

    if (loading && !pageActivities?.length) {
        return (
            <Layout>
                <S.FixContainer>
                    <Preloader minHeight="70vh" align="center" />
                </S.FixContainer>
            </Layout>
        );
    }

    if (!loading && !pageActivities?.length) {
        return (
            <Layout>
                <MetaWishList />
                <Empty />
            </Layout>
        );
    }

    return (
        <Layout>
            <MetaWishList />
            <S.FixContainer data-test-id="Wishlist">
                <S.WishHeader>Your Wishlist</S.WishHeader>
                <S.ActivitiesContainer>
                    {pageActivities.map((activity, index) => {
                        const description = getActivityDescription(activity as IActivity);
                        const newActivity = {
                            ...activity,
                            description,
                            city: {
                                name: activity.city_name || '',
                                id: activity.city_id || '',
                            },
                            category: {
                                name: activity.activity_categories[0]?.toString(),
                                id: activity.activity_categories[0]?.toString(),
                            },
                        };

                        return (
                            <ActivitiesPreview
                                key={activity.id}
                                activity={transformActivityToPreview(activity as IActivity)}
                                position={index}
                                listName="Wishlist"
                                userImages={userImages[`parent_${newActivity.id}`]}
                            />
                        );
                    })}
                </S.ActivitiesContainer>
                <PayPalSection />
            </S.FixContainer>
        </Layout>
    );
};
